"use client";

import { useEffect, useState } from "react";
import { Box, colors, Divider, Skeleton, Typography } from "@mui/material";
import Link from "next/link";
import api from "@v2/lib/api";
import { BlogPostType } from "@v2/types/blog";
import dayjs from "@v2/lib/dayjs";

interface Props {
    slug: string;
}

export default function BlogRelatedPosts({ slug }: Props) {
    const [posts, setPosts] = useState<BlogPostType[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(api.blog_post_related(slug) + "?limit=4")
            .then((r) => r.json())
            .then((d) => setPosts(d.data ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [slug]);

    if (!loading && !posts.length) return null;

    const items = loading ? Array.from({ length: 4 }) : posts;

    return (
        <Box sx={{ mb: 2, bgcolor: "#fff", borderRadius: 1.5, border: `1px solid ${colors.grey[100]}`, overflow: "hidden" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.grey[500], px: 2, py: 1.5 }}>
                مقالات مرتبط
            </Typography>
            <Divider />
            {items.map((post, i) => (
                <Box key={loading ? i : (post as BlogPostType).id}>
                    {loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5 }}>
                            <Skeleton variant="rounded" width={52} height={52} sx={{ borderRadius: 1.5, flexShrink: 0 }} />
                            <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" width="90%" />
                                <Skeleton variant="text" width="60%" />
                            </Box>
                        </Box>
                    ) : (
                        <Box
                            component={Link}
                            href={`/fa/blog/${(post as BlogPostType).slug}`}
                            prefetch={false}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                textDecoration: "none",
                                color: "inherit",
                                px: 2,
                                py: 1.5,
                                transition: "background-color 150ms ease",
                                "&:hover": { bgcolor: colors.grey[50] },
                            }}
                        >
                            {(post as BlogPostType).cover?.id ? (
                                <Box
                                    component="img"
                                    src={`${api.media}/${(post as BlogPostType).cover!.id}?action=cropThumbnail&width=64&height=64&quality=80`}
                                    alt={(post as BlogPostType).title}
                                    sx={{ width: 52, height: 52, borderRadius: 1.5, objectFit: "cover", flexShrink: 0 }}
                                />
                            ) : (
                                <Box sx={{ width: 52, height: 52, borderRadius: 1.5, bgcolor: colors.grey[100], flexShrink: 0 }} />
                            )}
                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: colors.grey[800], lineHeight: 1.5, WebkitLineClamp: 2, display: "-webkit-box", WebkitBoxOrient: "vertical", overflow: "hidden", mb: 0.5 }}>
                                    {(post as BlogPostType).title}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {(post as BlogPostType).published_at && (
                                        <Typography sx={{ fontSize: 11, color: colors.grey[600] }}>
                                            {dayjs((post as BlogPostType).published_at).locale("fa").format("D MMM YYYY")}
                                        </Typography>
                                    )}
                                    {(post as BlogPostType).reading_time && (
                                        <>
                                            <Box sx={{ width: 3, height: 3, borderRadius: "50%", bgcolor: colors.grey[300] }} />
                                            <Typography sx={{ fontSize: 11, color: colors.grey[600] }}>
                                                {(post as BlogPostType).reading_time} دقیقه مطالعه
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    )}
                    {i < items.length - 1 && <Divider />}
                </Box>
            ))}
        </Box>
    );
}
